import { create } from 'zustand';
import { collection, doc, getDoc, setDoc, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import { PilgrimSelectorDocument } from '../types/pilgrimSelector';
import toast from 'react-hot-toast';

type PilgrimSelectorState = {
  loading: boolean;
  error: string | null;
  progress: {
    current: number;
    total: number;
  };
  updateAllPilgrimSelectors: () => Promise<void>;
};

export const usePilgrimSelectorStore = create<PilgrimSelectorState>((set) => ({
  loading: false,
  error: null,
  progress: {
    current: 0,
    total: 0
  },

  updateAllPilgrimSelectors: async () => {
    set({ loading: true, error: null });
    console.time('Total Update Time');
    
    try {
      // Step 1: Fetch all required data in parallel
      console.log('Fetching all required data...');
      const [linksQuery, pilgrimsQuery, packagesQuery, guidesQuery, organizersQuery, selectorQuery, accommodationTablesQuery] = await Promise.all([
        getDocs(query(collection(firestore, 'PilgrimGuideLinks'), where('status', '==', 'approved'))),
        getDocs(collection(firestore, 'Pilgrims')),
        getDocs(collection(firestore, 'Packages')),
        getDocs(collection(firestore, 'Guides')),
        getDocs(collection(firestore, 'Organizers')),
        getDocs(collection(firestore, 'PilgrimSelector')),
        getDocs(collection(firestore, 'AccommodationTables'))
      ]);

      // Step 2: Create lookup maps
      console.log('Creating lookup maps...');
      const pilgrims = new Map(pilgrimsQuery.docs.map(doc => [doc.id, doc.data()]));
      const packages = new Map(packagesQuery.docs.map(doc => [doc.data().name, doc.data()]));
      const guides = new Map(guidesQuery.docs.map(doc => [doc.id, doc.data()]));
      const organizers = new Map(organizersQuery.docs.map(doc => [doc.id, doc.data()]));
      const existingSelectors = new Map(selectorQuery.docs.map(doc => [doc.id, doc.data()]));

      // Create map of actual pilgrim accommodations by scanning all accommodation tables
      console.log('Scanning accommodation tables for actual assignments...');
      const actualAccommodations = new Map<string, Set<string>>();
      
      accommodationTablesQuery.docs.forEach(doc => {
        const table = doc.data();
        if (!table.r || !Array.isArray(table.r)) return;
        
        console.log(`Scanning table ${table.tn} (${table.hn}) with ${table.r.length} rooms`);
        
        // Keep track of how many pilgrims we find accommodated in this table
        let pilgrimsFoundCount = 0;
        
        table.r.forEach((room: any) => {
          if (!room.b || !Array.isArray(room.b)) return;
          
          room.b.forEach((bed: any) => {
            if (!bed.a || !Array.isArray(bed.a)) return;
            
            bed.a.forEach((assignment: any) => {
              // Make sure pilgrimId exists in the assignment
              if (!assignment.pid) return;
              
              // Add this hotel to the pilgrim's set of actually accommodated hotels
              if (!actualAccommodations.has(assignment.pid)) {
                actualAccommodations.set(assignment.pid, new Set());
              }
              
              actualAccommodations.get(assignment.pid)?.add(table.hn);
              pilgrimsFoundCount++;
            });
          });
        });
        
        console.log(`Found ${pilgrimsFoundCount} pilgrims accommodated in ${table.hn}`);
      });
      
      console.log(`Found actual accommodations for ${actualAccommodations.size} unique pilgrims`);

      // Step 3: Process links in batches
      console.log('Processing links in batches...');
      const WRITE_BATCH_SIZE = 450;
      let currentBatch = writeBatch(firestore);
      let batchCount = 0;
      let processedCount = 0;
      let successCount = 0;
      let errorCount = 0;

      set({ progress: { current: 0, total: linksQuery.docs.length } });

      // Process all links
      for (const linkDoc of linksQuery.docs) {
        try {
          const linkData = linkDoc.data();
          const pilgrimData = pilgrims.get(linkData.pilgrimId);
          
          if (!pilgrimData) {
            throw new Error(`Pilgrim ${linkData.pilgrimId} not found`);
          }

          const packageData = packages.get(pilgrimData.package_nickName);
          if (!packageData) {
            throw new Error(`Package "${pilgrimData.package_nickName}" not found`);
          }

          const guideData = guides.get(linkData.guideId);
          if (!guideData) {
            throw new Error(`Guide ${linkData.guideId} not found`);
          }

          const organizerData = organizers.get(guideData.organizerId);
          if (!organizerData) {
            throw new Error(`Organizer ${guideData.organizerId} not found`);
          }

          // Get existing selector data if it exists
          const existingSelector = existingSelectors.get(linkData.pilgrimId);

          // Initialize accommodation status with package hotels
          // Step 1: Start with all hotels from package set to false
          const accommodationStatus: Record<string, boolean> = {};
          
          // Add main Makkah hotel
          accommodationStatus[packageData.mainMakkahHotel] = false;
          
          // Add shifting Makkah hotel if exists
          if (packageData.shiftingMakkahHotel) {
            accommodationStatus[packageData.shiftingMakkahHotel] = false;
          }
          
          // Add Madinah hotel
          accommodationStatus[packageData.madinahHotel] = false;
          
          // Step 2: Update with actual accommodation status from tables
          const pilgrimActualHotels = actualAccommodations.get(linkData.pilgrimId);
          if (pilgrimActualHotels) {
            // Only set hotels to true if they're actually accommodated there
            for (const hotelName of pilgrimActualHotels) {
              if (accommodationStatus.hasOwnProperty(hotelName)) {
                accommodationStatus[hotelName] = true;
              }
            }
          }
          
          // Log the accommodation status for debugging
          console.log(`Accommodation status for pilgrim ${linkData.pilgrimId}:`, accommodationStatus);

          // --- USE STRING DATES INSTEAD OF TIMESTAMPS ---
          
          // Collect all hotels the pilgrim is assigned to
          const hotels = [
            packageData.mainMakkahHotel,
            packageData.madinahHotel
          ];
          
          if (packageData.shiftingMakkahHotel) {
            hotels.push(packageData.shiftingMakkahHotel);
          }
          
          // Create hotelRooms mapping with string dates
          const hotelRooms: Record<string, {
            roomType: 'double' | 'triple' | 'quadruple';
            startDate: string;
            endDate: string;
          }> = {};
          
          // Main Makkah
          hotelRooms[packageData.mainMakkahHotel] = {
            roomType: pilgrimData.main_makkah_roomType,
            startDate: packageData.mainMakkahStartDate, // Already string format DD/MM/YYYY
            endDate: packageData.mainMakkahEndDate     // Already string format DD/MM/YYYY
          };
          
          // Madinah
          hotelRooms[packageData.madinahHotel] = {
            roomType: pilgrimData.madinah_roomType,
            startDate: packageData.madinahStartDate,   // Already string format DD/MM/YYYY
            endDate: packageData.madinahEndDate       // Already string format DD/MM/YYYY
          };
          
          // Shifting Makkah (if exists)
          if (packageData.shiftingMakkahHotel) {
            hotelRooms[packageData.shiftingMakkahHotel] = {
              roomType: pilgrimData.shifting_makkah_roomType,
              startDate: packageData.shiftingMakkahStartDate, // Already string format DD/MM/YYYY
              endDate: packageData.shiftingMakkahEndDate     // Already string format DD/MM/YYYY
            };
          }
          
          // USE STRING DATES FOR checkInDates and checkOutDates instead of timestamps
          const checkInDates: Record<string, string> = {};
          const checkOutDates: Record<string, string> = {};
          
          // Main Makkah dates (as strings)
          checkInDates[packageData.mainMakkahHotel] = packageData.mainMakkahStartDate; 
          checkOutDates[packageData.mainMakkahHotel] = packageData.mainMakkahEndDate;
          
          // Madinah dates (as strings)
          checkInDates[packageData.madinahHotel] = packageData.madinahStartDate;
          checkOutDates[packageData.madinahHotel] = packageData.madinahEndDate;
          
          // Shifting Makkah dates (as strings, if exists)
          if (packageData.shiftingMakkahHotel) {
            checkInDates[packageData.shiftingMakkahHotel] = packageData.shiftingMakkahStartDate;
            checkOutDates[packageData.shiftingMakkahHotel] = packageData.shiftingMakkahEndDate;
          }
          
          // Create search tags 
          const normalizeText = (text: any) => {
            if (text === null || text === undefined) {
              return '';
            }
            return typeof text === 'string' ? text.toLowerCase()
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
              .replace(/[^\w\s]/g, '') : String(text);
          };
          
          const searchTags = [
            normalizeText(pilgrimData.Name),
            String(pilgrimData.Passport_Number),
            normalizeText(pilgrimData.Residence_Country),
            normalizeText(pilgrimData.package_nickName),
            normalizeText(pilgrimData.nusuk_group || ''),
            normalizeText(pilgrimData.Pilgrim_Type || '')
          ].filter(tag => {
            if (typeof tag === 'string') {
              return tag.trim().length > 0;
            }
            return !!tag;
          });
          
          // Create accommodation status tags
          const accommodationStatusTags = Object.entries(accommodationStatus).map(
            ([hotel, status]) => `${hotel}:${status}`
          );

          // Create selector document
          const selectorDoc: PilgrimSelectorDocument = {
            id: linkData.pilgrimId,
            pilgrimId: linkData.pilgrimId,
            guideId: linkData.guideId,
            organizerId: guideData.organizerId,

            pilgrimData: {
              name: pilgrimData.Name,
              dateOfBirth: pilgrimData.Date_of_Birth,
              gender: pilgrimData.Gender.toLowerCase(),
              passportNumber: pilgrimData.Passport_Number,
              pilgrimType: pilgrimData.Pilgrim_Type,
              residenceCountry: pilgrimData.Residence_Country,
              nusukGroup: pilgrimData.nusuk_group,
              packageNickName: pilgrimData.package_nickName,
              pilgrimCategory: pilgrimData.pilgrim_category,
            },

            packageData: {
              packageName: packageData.name,
              madinah: {
                hotel: packageData.madinahHotel,
                startDate: packageData.madinahStartDate,
                endDate: packageData.madinahEndDate,
                roomType: pilgrimData.madinah_roomType,
              },
              mainMakkah: {
                hotel: packageData.mainMakkahHotel,
                startDate: packageData.mainMakkahStartDate,
                endDate: packageData.mainMakkahEndDate,
                roomType: pilgrimData.main_makkah_roomType,
              },
              ...(packageData.shiftingMakkahHotel ? {
                shiftingMakkah: {
                  hotel: packageData.shiftingMakkahHotel,
                  startDate: packageData.shiftingMakkahStartDate,
                  endDate: packageData.shiftingMakkahEndDate,
                  roomType: pilgrimData.shifting_makkah_roomType,
                }
              } : {}),
            },

            guideData: {
              name: guideData.name,
              organizerId: guideData.organizerId,
            },

            organizerData: {
              name: organizerData.name,
            },

            createdAt: existingSelector?.createdAt || Date.now(),
            updatedAt: Date.now(),
            status: 'active',
            isAccommodated: accommodationStatus,
            
            // NEW fields for efficient querying
            hotels,
            hotelRooms,
            checkInDates,  // Now using string dates
            checkOutDates, // Now using string dates
            searchTags,
            accommodationStatus: accommodationStatusTags
          };

          // Add to batch
          currentBatch.set(doc(firestore, 'PilgrimSelector', linkData.pilgrimId), selectorDoc);
          batchCount++;
          successCount++;

          // Commit batch if it reaches size limit
          if (batchCount >= WRITE_BATCH_SIZE) {
            await currentBatch.commit();
            currentBatch = writeBatch(firestore);
            batchCount = 0;
          }

        } catch (error) {
          console.error(`Error processing link:`, error);
          errorCount++;
        }

        processedCount++;
        set({ progress: { current: processedCount, total: linksQuery.docs.length } });
      }

      // Commit any remaining documents
      if (batchCount > 0) {
        await currentBatch.commit();
      }

      console.timeEnd('Total Update Time');
      console.log('Final Statistics:', {
        totalProcessed: processedCount,
        successful: successCount,
        errors: errorCount,
        actualAccommodations: actualAccommodations.size
      });

      toast.success(`Updated ${successCount} pilgrim selectors${errorCount > 0 ? ` (${errorCount} errors)` : ''}`);
    } catch (error: any) {
      console.error('Error updating selectors:', error);
      set({ error: error.message, loading: false });
      toast.error('Failed to update pilgrim selectors');
      throw error;
    } finally {
      set({ loading: false });
    }
  }
}));